/**
 * Widget telemetry helpers — session id + event beacon.
 *
 * Why a dedicated module (and not inlined in each caller):
 *   • Session ID must be *stable* across the three widget surfaces (CTA,
 *     Beden Tablosu modal, Bedenimi Bul wizard) so backend analytics can
 *     reconstruct the funnel per visitor. We store it on `sessionStorage`
 *     — survives SPA navigations and iframe refreshes, dies with the tab.
 *   • `navigator.sendBeacon` is the right tool for unload-safe event
 *     posting (fires even if the user clicks away immediately after modal
 *     open). We also fall back to `fetch(keepalive:true)` for Safari <14.
 *   • Keeping both helpers in one tiny file (no deps) means the Shadow
 *     DOM widget bundle picks it up for free — no new vite chunk weight.
 *
 * Test environment detection (`isSayzTestEnv()`):
 *   The embed script sits on the merchant's live site, so we cannot
 *   distinguish prod traffic from staging/dev traffic by domain. We
 *   trust TWO explicit signals, matching the public billing policy:
 *     • `?sayz_test=1` on the current page or referer URL
 *     • The current page is served from the Sayz panel itself
 *       (`/app/*` or `/panel/*`), i.e. live preview inside the admin UI
 *   Any event flagged `is_test:true` is logged (so merchant can audit
 *   it on the Trafik tab) but never counted toward quota.
 *
 * Non-goals:
 *   • NOT a persistent "user id". Session-scoped, cleared on tab close
 *     — compliant with cookie-free analytics for GDPR.
 *   • NOT a replacement for `widget_errors` sink (`/api/widget/error`).
 *     Errors go through their own endpoint; this one is for business
 *     funnel events + algorithm input logging.
 */

const SESSION_STORAGE_KEY = "sayz_session_id";

/** Generate a RFC-4122 v4 UUID using the crypto API when available. */
function uuid4() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        try { return crypto.randomUUID(); } catch (_) { /* fall through */ }
    }
    // Fallback for very old browsers — not cryptographically strong but
    // sufficient for analytics session scoping. Collision risk is near zero
    // within a single tab's lifetime.
    const rand = (n) => Math.floor(Math.random() * n);
    const hex = (n, w) => n.toString(16).padStart(w, "0");
    return `${hex(rand(0xffffffff), 8)}-${hex(rand(0xffff), 4)}-4${hex(rand(0x0fff), 3)}-${hex(0x8 | rand(0x3), 1)}${hex(rand(0x0fff), 3)}-${hex(rand(0xffffffffffff), 12)}`;
}

/**
 * Return a stable session id for this browser tab.
 * Creates one the first time it's called, caches on sessionStorage.
 * Safe to call from any widget surface — no race, idempotent.
 */
export function getSayzSessionId() {
    try {
        if (typeof sessionStorage === "undefined") return uuid4();
        let sid = sessionStorage.getItem(SESSION_STORAGE_KEY);
        if (!sid) {
            sid = uuid4();
            sessionStorage.setItem(SESSION_STORAGE_KEY, sid);
        }
        return sid;
    } catch (_) {
        // Private browsing or storage disabled — still return a per-call id
        // so backend aggregations don't crash. These visits just won't
        // deduplicate across modal open ↔ suggest events, which is fine.
        return uuid4();
    }
}

/**
 * Detect whether this event is from a non-billable test environment.
 *
 * Returns true when ANY of:
 *   • Current URL has `?sayz_test=1` (merchant's own staging/preview URLs)
 *   • Referer URL has `?sayz_test=1` (iframe/widget inside tagged page)
 *   • Path starts with `/app/` or `/panel/` (Sayz panel live preview)
 *
 * Memoized per page load — resolve once, reuse across every beacon.
 */
let _testEnvCache = null;
export function isSayzTestEnv() {
    if (_testEnvCache !== null) return _testEnvCache;
    try {
        if (typeof window === "undefined") {
            _testEnvCache = false;
            return false;
        }
        const search = (window.location && window.location.search) || "";
        const path = (window.location && window.location.pathname) || "";
        const ref = (typeof document !== "undefined" && document.referrer) || "";
        const hasFlag = (q) => /[?&]sayz_test=1(?:&|$)/.test(q || "");
        const refQuery = ref.indexOf("?") >= 0 ? ref.slice(ref.indexOf("?")) : "";
        const result =
            hasFlag(search) ||
            hasFlag(refQuery) ||
            path.startsWith("/app/") ||
            path.startsWith("/panel/");
        _testEnvCache = result;
        return result;
    } catch (_) {
        _testEnvCache = false;
        return false;
    }
}

/**
 * Fire-and-forget analytics beacon.
 *
 * `apiBase` is the merchant site's `window.location.origin` for the live
 * widget bundle, or the panel's REACT_APP_BACKEND_URL for the admin preview.
 * `event` ∈ { "modal_open", "size_suggested", "size_accepted" }.
 * `payload` carries { widget, sku, size?, size_in?, ... } extras.
 *
 * Return value: boolean (true = beacon enqueued, false = silently dropped).
 * Never throws — we never let analytics break the merchant's checkout flow.
 */
export function sendSayzEvent(apiBase, event, payload = {}) {
    if (!apiBase || !event) return false;
    try {
        const body = JSON.stringify({
            ...payload,
            event,
            session_id: getSayzSessionId(),
            is_test: isSayzTestEnv(),
            ts: Date.now(),
        });
        const url = `${apiBase}/api/widget/event`;

        // Primary path: `sendBeacon` — survives page unload. We ship the
        // JSON body as a `text/plain` Blob to keep the request on the CORS
        // "simple" path: text/plain is one of the three Content-Type values
        // that do NOT trigger a preflight (vs application/json which DOES).
        // Avoiding preflight is critical here because `sendBeacon` ALWAYS
        // sends with credentials (cookies) and some browsers surface that
        // to the server CORS check as `credentials: include` — the preflight
        // would then require `Access-Control-Allow-Credentials: true` on
        // our embed endpoints, which we intentionally do not set (public,
        // anonymous beacons). Backend's `/api/widget/event` reads the raw
        // body as JSON regardless of the declared type, so the mime switch
        // is purely a browser-contract concern.
        if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
            try {
                const blob = new Blob([body], { type: "text/plain" });
                const ok = navigator.sendBeacon(url, blob);
                if (ok) return true;
                // Some browsers return false if the body is "too large" or
                // the user is offline — fall through to fetch keepalive.
            } catch (_) { /* fall through */ }
        }

        // Fallback: fetch with keepalive. Fire-and-forget — no await. We
        // also use `text/plain` here for consistency with the beacon path
        // (no preflight, no credentials dance). Backend tolerates both.
        if (typeof fetch === "function") {
            fetch(url, {
                method: "POST",
                headers: { "Content-Type": "text/plain" },
                body,
                keepalive: true,
                // Public endpoint, no auth cookies needed.
                credentials: "omit",
            }).catch(() => { /* swallow */ });
            return true;
        }
    } catch (_) { /* swallow — never crash host page */ }
    return false;
}
